<template>
    <div class="md:hidden">
        <div
            v-if="loading"
            class="relative h-64 flex items-center justify-center"
        >
            <LoadingState :loading="true" size="large" :noBackground="true" />
        </div>

        <div v-else>
            <div
                class="flex items-center gap-2 px-4 py-3 rounded-t-xl bg-[#FD421D] text-white shadow-sm w-full relative z-0 mt-2 -mb-2 overflow-hidden"
            >
                <icon icon="sparkles" class="w-4 h-4 shrink-0" />
                <span class="text-white text-sm font-medium truncate">
                    {{ user && user.name ? `${user.name}'s` : 'Your' }} top
                    recommended card
                </span>
            </div>

            <div
                class="w-full rounded-lg bg-white dark:bg-slate-800 shadow dark:shadow-slate-900/50 overflow-hidden relative z-20"
            >
                <div class="p-4">
                    <div class="flex justify-between items-center mb-4">
                        <div class="flex items-center gap-2">
                            <icon
                                icon="card-recommended"
                                class="w-5 h-5 text-slate-600 dark:text-slate-400"
                            />
                            <h2 class="text-slate-800 dark:text-slate-100 text-lg font-medium">
                                My next card
                            </h2>
                        </div>
                        <a
                            href="/mynextcard/"
                            class="flex items-center gap-1 text-sm font-medium text-slate-800 dark:text-slate-200 hover:text-slate-700 dark:hover:text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0 rounded"
                        >
                            View all
                            <icon icon="arrow" class="w-4 h-4" />
                        </a>
                    </div>
                    <div
                        class="flex flex-col items-center self-stretch pt-4 px-8 pb-6"
                    >
                        <a
                            :href="`/mynextcard/card/${card?.id}`"
                            class="block w-full rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors duration-200"
                        >
                            <img
                                :src="`${cdnUrl}/${card?.card_image?.path}/_small/${card?.card_image?.file_name}`"
                                :alt="
                                    card?.title || 'Top Recommended Credit Card'
                                "
                                class="w-full h-auto object-contain"
                            />
                        </a>
                    </div>

                    <h3
                        class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7 mt-5"
                    >
                        {{ card?.title }}
                    </h3>

                    <div class="mt-6">
                        <div
                            class="flex items-center gap-2"
                            v-if="
                                card?.welcome_offer_amount &&
                                card?.welcome_offer_program
                            "
                        >
                            <span
                                class="text-orange text-base font-medium leading-6"
                            >
                                {{
                                    formatWelcomeAmount(
                                        card.welcome_offer_amount
                                    )
                                }}
                            </span>
                            <span
                                class="flex-1 text-slate-800 dark:text-slate-100 text-base font-medium leading-6"
                            >
                                {{ card.welcome_offer_program }}
                            </span>
                        </div>
                        <div
                            class="text-slate-600 dark:text-slate-400 text-sm font-normal leading-5 mt-1"
                            v-safe-html="card?.welcome_offer"
                        ></div>
                    </div>

                    <div class="mt-6" v-if="earnRateTokens.length">
                        <div class="flex items-start gap-2">
                            <icon
                                icon="points"
                                class="w-5 h-5 text-slate-600 dark:text-slate-400"
                            />
                            <span
                                class="text-slate-600 dark:text-slate-400 text-sm font-medium leading-5"
                            >
                                Earn points
                            </span>
                        </div>
                        <div
                            class="flex items-start content-start gap-x-6 gap-y-4 flex-wrap mt-2"
                        >
                            <div
                                v-for="rate in earnRateTokens"
                                :key="rate.multiplier + '-' + rate.category"
                                class="flex items-center gap-2"
                            >
                                <div
                                    class="flex px-2 py-0.5 rounded bg-orange-50"
                                >
                                    <span
                                        class="text-orange text-sm font-semibold leading-5"
                                    >
                                        {{ rate.multiplier }}
                                    </span>
                                </div>
                                <span class="text-slate-800 dark:text-slate-100 text-sm leading-5">
                                    {{ rate.category }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="mt-6">
                        <div class="flex items-start gap-2">
                            <icon
                                icon="base-coins"
                                class="w-5 h-5 text-slate-600 dark:text-slate-400"
                            />
                            <span
                                class="text-slate-600 dark:text-slate-400 text-sm font-medium leading-5"
                            >
                                Annual fee
                            </span>
                        </div>
                        <div class="flex items-center gap-1 mt-1">
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                ${{ card?.annual_fee }}
                            </span>
                            <template
                                v-if="
                                    card?.issuing_bank &&
                                    (card?.issuing_bank?.name ===
                                        'American Express' ||
                                        card?.issuing_bank?.name === 'Citi') &&
                                    card?.rates_and_fees_link
                                "
                            >
                                <a
                                    :href="card?.rates_and_fees_link"
                                    target="_blank"
                                    class="text-slate-600 dark:text-slate-400 text-sm font-normal leading-5 hover:text-slate-800 dark:hover:text-slate-200"
                                >
                                    (Rates and fees)
                                </a>
                            </template>
                        </div>
                    </div>

                    <div class="flex flex-col items-stretch gap-2 mt-6">
                        <a
                            v-if="card?.application_link"
                            :href="applicationLinkWithMnc"
                            target="_blank"
                            class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-orange shadow-sm hover:bg-orange-600 transition-colors duration-200"
                        >
                            <span
                                class="text-white text-sm font-medium leading-5"
                            >
                                Learn How to Apply
                            </span>
                            <icon
                                icon="arrow-right-alt"
                                class="w-5 h-5 text-white"
                            />
                        </a>

                        <a
                            :href="`/mynextcard/card/${card?.id}`"
                            class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600"
                        >
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                Read our take
                            </span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div
        class="hidden md:flex w-full p-6 pb-4 flex-col items-start gap-4 rounded-lg bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700"
    >
        <div class="flex justify-between items-center self-stretch mb-4">
            <div class="flex items-center gap-2">
                <icon icon="card-recommended" class="w-5 h-5 text-slate-800 dark:text-slate-200" />
                <h2 class="text-slate-800 dark:text-slate-100 text-lg font-medium">My next card</h2>
            </div>
            <a
                href="/mynextcard/"
                class="flex items-center gap-1 text-sm font-medium text-slate-800 dark:text-slate-200 hover:text-slate-700 dark:hover:text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0 rounded"
            >
                View all
                <icon icon="arrow" class="w-4 h-4" />
            </a>
        </div>

        <div
            v-if="loading"
            class="relative h-64 flex items-center justify-center self-stretch"
        >
            <LoadingState :loading="true" size="large" :noBackground="true" />
        </div>

        <div v-else class="flex items-start gap-12 self-stretch">
            <div class="flex flex-col w-80 min-w-[320px] relative">
                <div
                    class="inline-flex items-center gap-2 px-4 py-3 rounded-t-xl bg-[#FD421D] text-white absolute -top-4 left-0 z-10 max-w-[280px] overflow-hidden"
                    style="box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)"
                >
                    <icon icon="sparkles" class="w-4 h-4 shrink-0" />
                    <span class="text-white text-sm font-medium truncate">
                        {{ user && user.name ? `${user.name}'s` : 'Your' }} top
                        recommended card
                    </span>
                </div>

                <div
                    class="w-80 h-[198px] rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-700 flex items-center justify-center relative z-20 mt-6"
                    style="
                        box-shadow:
                            0 8px 25px rgba(0, 0, 0, 0.15),
                            0 4px 10px rgba(0, 0, 0, 0.1);
                        transform: translateY(-2px);
                    "
                >
                    <img
                        :src="`${cdnUrl}/${card?.card_image?.path}/_small/${card?.card_image?.file_name}`"
                        :alt="card?.title || 'Top Recommended Credit Card'"
                        class="w-full h-full object-contain"
                    />
                </div>

                <div class="flex flex-col items-start gap-2 w-full mt-4">
                    <a
                        v-if="card?.application_link"
                        :href="applicationLinkWithMnc"
                        target="_blank"
                        class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-orange shadow-sm hover:bg-orange-600 transition-colors duration-200"
                    >
                        <span class="text-white text-sm font-medium leading-5">
                            Learn How to Apply
                        </span>
                        <icon
                            icon="arrow-right-alt"
                            class="w-5 h-5 text-white"
                        />
                    </a>

                    <a
                        :href="`/mynextcard/card/${card?.id}`"
                        class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600"
                    >
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            Read our take
                        </span>
                    </a>
                </div>
            </div>

            <div class="flex pt-4 flex-col justify-center items-start flex-1">
                <div class="flex pb-2 flex-col items-start self-stretch">
                    <h3 class="text-slate-800 dark:text-slate-100 text-2xl font-medium leading-8">
                        {{ card?.title }}
                    </h3>
                </div>

                <div class="flex pb-4 flex-col items-start gap-1 self-stretch">
                    <div
                        class="flex items-center gap-2"
                        v-if="
                            card?.welcome_offer_amount &&
                            card?.welcome_offer_program
                        "
                    >
                        <span
                            class="text-orange text-base font-medium leading-6"
                        >
                            {{ formatWelcomeAmount(card.welcome_offer_amount) }}
                        </span>
                        <span
                            class="flex-1 text-slate-800 dark:text-slate-100 text-base font-medium leading-6"
                        >
                            {{ card.welcome_offer_program }}
                        </span>
                    </div>
                    <div
                        class="self-stretch text-slate-600 dark:text-slate-400 text-sm font-normal leading-5"
                        v-safe-html="card?.welcome_offer"
                    ></div>
                </div>

                <div
                    class="flex pb-6 flex-col items-start gap-2 self-stretch"
                    v-if="earnRateTokens.length"
                >
                    <div class="flex items-start gap-2">
                        <icon icon="points" class="w-5 h-5 text-slate-600 dark:text-slate-400" />
                        <span
                            class="text-slate-600 dark:text-slate-400 text-sm font-medium leading-5"
                        >
                            Earn points
                        </span>
                    </div>
                    <div
                        class="flex items-start content-start gap-x-6 gap-y-4 self-stretch flex-wrap"
                    >
                        <div
                            v-for="rate in earnRateTokens"
                            :key="rate.multiplier + '-' + rate.category"
                            class="flex items-center gap-2"
                        >
                            <div class="flex px-2 py-0.5 rounded bg-orange-50">
                                <span
                                    class="text-orange text-sm font-semibold leading-5"
                                >
                                    {{ rate.multiplier }}
                                </span>
                            </div>
                            <span class="text-slate-800 dark:text-slate-100 text-sm leading-5">
                                {{ rate.category }}
                            </span>
                        </div>
                    </div>
                </div>

                <div
                    class="flex flex-col justify-center items-start gap-2 self-stretch"
                >
                    <div class="flex items-start gap-2">
                        <icon
                            icon="base-coins"
                    class="w-5 h-5 text-slate-600 dark:text-slate-400"
                        />
                        <span
                            class="text-slate-600 dark:text-slate-400 text-sm font-medium leading-5"
                        >
                            Annual fee
                        </span>
                    </div>
                    <div class="flex items-center gap-1">
                        <span
                            class="text-slate-800 dark:text-slate-100 text-center text-sm font-medium leading-5"
                        >
                            ${{ card?.annual_fee }}
                        </span>
                        <template
                            v-if="
                                card?.issuing_bank &&
                                (card?.issuing_bank?.name ===
                                    'American Express' ||
                                    card?.issuing_bank?.name === 'Citi') &&
                                card?.rates_and_fees_link
                            "
                        >
                            <a
                                :href="card?.rates_and_fees_link"
                                target="_blank"
                                class="text-slate-600 dark:text-slate-400 text-sm font-normal leading-5 hover:text-slate-800 dark:hover:text-slate-200"
                            >
                                (Rates and fees)
                            </a>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState } from 'pinia';
import { useDashboardStore } from '@/store/dashboard';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import Icon from '../../ui/icon/icon.vue';
import { appendMncToUrl } from '@/utils/urlHelpers.js';

export default {
    props: [],
    components: {
        LoadingState,
        Icon,
    },
    computed: {
        ...mapState(useDashboardStore, ['loading', 'card', 'user']),
        parsedPointsTips() {
            const earnRates = this.card && this.card.earn_rates;
            if (!earnRates) return [];
            if (Array.isArray(earnRates)) return earnRates;
            try {
                return JSON.parse(earnRates);
            } catch (e) {
                return [];
            }
        },
        earnRateTokens() {
            const items = this.parsedPointsTips
                .filter(
                    (t) =>
                        t &&
                        (t.label ||
                            t.value ||
                            t.earnMultiplier ||
                            t.spendBonusDesc)
                )
                .map((t) => {
                    const rawMultiplier = t.earnMultiplier || t.label || '';

                    let category = '';
                    if (t.spendBonusCategoryName) {
                        category = t.spendBonusCategoryName;
                    } else if (t.spendBonusDesc || t.value) {
                        const textToDerive = t.spendBonusDesc || t.value || '';
                        category = this.deriveCategoryLabel(
                            textToDerive.toString()
                        );
                    }

                    const multiplier = this.normalizeMultiplier(
                        rawMultiplier.toString()
                    );
                    const value = this.extractMultiplierValue(multiplier);
                    return { multiplier, category, value };
                })
                .filter((r) => r.multiplier || r.category);
            return items.sort((a, b) => b.value - a.value);
        },
        applicationLinkWithMnc() {
            return appendMncToUrl(this.card?.application_link);
        },
    },
    data: () => ({
        cdnUrl: '',
    }),
    methods: {
        formatWelcomeAmount(amount) {
            if (!amount) return '';
            return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        deriveCategoryLabel(text) {
            if (!text) return '';
            let t = String(text)
                .replace(/<[^>]*>/g, '')
                .trim();
            const earnIdx = t.toLowerCase().indexOf(' on ');
            if (earnIdx !== -1) {
                t = t.slice(earnIdx + 4).trim();
            }
            t = t.replace(/\.$/, '').trim();
            t = t.replace(/\s{2,}/g, ' ');
            if (t.length > 60) {
                t = t.slice(0, 57).trim() + '...';
            }
            return t.charAt(0).toUpperCase() + t.slice(1);
        },
        normalizeMultiplier(text) {
            if (!text) return '';
            const str = String(text).trim();
            const m = str.match(/([0-9]+(?:\.[0-9]+)?)\s*[xX]/);
            if (m) {
                return `${m[1]}x`;
            }
            const numMatch = str.match(/^([0-9]+(?:\.[0-9]+)?)$/);
            if (numMatch) {
                return `${numMatch[1]}x`;
            }
            return str.toLowerCase();
        },
        extractMultiplierValue(multiplier) {
            const m = String(multiplier).match(/([0-9]+(?:\.[0-9]+)?)/);
            return m ? parseFloat(m[1]) : 0;
        },
    },
    mounted() {
        this.cdnUrl = import.meta.env.VITE_DO_CDN_ENDPOINT;
    },
};
</script>
<style scoped>
:deep(.prose h3) {
    margin-bottom: 0px !important;
    margin-top: 0px !important;
    position: relative;
    padding-left: 24px;
    color: #1e293b;
}

:deep(.prose h3::before) {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='points'%3E%3Cpath id='vector' fill-rule='evenodd' clip-rule='evenodd' d='M5.00398 4.58341C5.01804 4.61107 5.05738 4.6698 5.1614 4.75763C5.35252 4.919 5.67847 5.10187 6.15419 5.27177C7.09837 5.60898 8.45753 5.83342 9.9987 5.83342C11.5399 5.83342 12.899 5.60898 13.8432 5.27177C14.3189 5.10187 14.6449 4.919 14.836 4.75763C14.94 4.6698 14.9794 4.61107 14.9934 4.58341C14.9794 4.55576 14.94 4.49703 14.836 4.4092C14.6449 4.24783 14.3189 4.06496 13.8432 3.89506C12.899 3.55785 11.5399 3.33341 9.9987 3.33341C8.45753 3.33341 7.09837 3.55785 6.15419 3.89506C5.67847 4.06496 5.35252 4.24783 5.1614 4.4092C5.05738 4.49703 5.01804 4.55576 5.00398 4.58341ZM14.9987 6.5978C14.8092 6.68678 14.6097 6.76778 14.4038 6.84134C13.2367 7.25815 11.6792 7.50008 9.9987 7.50008C8.31821 7.50008 6.7607 7.25815 5.59363 6.84134C5.38765 6.76778 5.18818 6.68678 4.9987 6.5978V8.11342C5.00625 8.13306 5.03916 8.19608 5.1614 8.2993C5.35252 8.46067 5.67847 8.64354 6.15419 8.81344C7.09837 9.15064 8.45753 9.37508 9.9987 9.37508C11.5399 9.37508 12.899 9.15064 13.8432 8.81344C14.3189 8.64354 14.6449 8.46067 14.836 8.2993C14.9582 8.19608 14.9911 8.13306 14.9987 8.11342V6.5978ZM14.9987 10.1395C14.8092 10.2284 14.6097 10.3094 14.4038 10.383C13.2367 10.7998 11.6792 11.0417 9.9987 11.0417C8.31821 11.0417 6.7607 10.7998 5.59363 10.383C5.38765 10.3094 5.18818 10.2284 4.9987 10.1395V11.6551C5.00625 11.6747 5.03916 11.7377 5.1614 11.841C5.35252 12.0023 5.67847 12.1852 6.15419 12.3551C7.09837 12.6923 8.45753 12.9167 9.9987 12.9167C11.5399 12.9167 12.899 12.6923 13.8432 12.3551C14.3189 12.1852 14.6449 12.0023 14.836 11.841C14.9582 11.7377 14.9912 11.6747 14.9987 11.6551V10.1395ZM14.9987 13.6811C14.8092 13.7701 14.6097 13.8511 14.4038 13.9247C13.2367 14.3415 11.6792 14.5834 9.9987 14.5834C8.31821 14.5834 6.7607 14.3415 5.59363 13.9247C5.38765 13.8511 5.18818 13.7701 4.9987 13.6811V15.4051C5.00625 15.4247 5.03916 15.4877 5.1614 15.591C5.35252 15.7523 5.67847 15.9352 6.15419 16.1051C7.09837 16.4423 8.45753 16.6667 9.9987 16.6667C11.5399 16.6667 12.899 16.4423 13.8432 16.1051C14.3189 15.9352 14.6449 15.7523 14.836 15.591C14.9582 15.4877 14.9911 15.4247 14.9987 15.4051V13.6811ZM16.6654 15.4167C16.6654 16.0448 16.3066 16.5305 15.9112 16.8644C15.512 17.2015 14.9837 17.4676 14.4038 17.6747C13.2367 18.0915 11.6792 18.3334 9.9987 18.3334C8.31821 18.3334 6.7607 18.0915 5.59363 17.6747C5.01372 17.4676 4.4854 17.2015 4.08615 16.8644C3.69076 16.5305 3.33203 16.0448 3.33203 15.4167V4.58341C3.33203 3.95534 3.69076 3.46963 4.08615 3.13578C4.4854 2.79865 5.01372 2.5326 5.59363 2.32549C6.7607 1.90868 8.31821 1.66675 9.9987 1.66675C11.6792 1.66675 13.2367 1.90868 14.4038 2.32549C14.9837 2.5326 15.512 2.79865 15.9112 3.13578C16.3066 3.46963 16.6654 3.95534 16.6654 4.58341V15.4167Z' fill='%231E293B'/%3E%3C/g%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
}

/* Welcome Offer Styling */
:deep(.welcome-offer-content) {
    .current-offer {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        margin-bottom: 0;
    }

    h4 {
        flex: 1 0 0;
        color: #1e293b;
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
        margin-bottom: 0;

        span.text-orange {
            color: #fd421d;
            font-size: 16px;
            font-weight: 500;
            line-height: 24px;
        }
    }

    p {
        align-self: stretch;
        color: #64748b;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        margin-bottom: 0;
    }
}
</style>
